import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './EmployeeManagement.css';
import EmployeeList from './EmployeeList';
import EmployeeForm from './EmployeeForm';
import DocumentManager from './DocumentManager';
import LeaveRequestForm from './LeaveRequestForm';
import LeaveBalance from './LeaveBalance';
import ModulePermissionDialog from './ModulePermissionDialog';
import EmployeeDefaultBonusEditor from './EmployeeDefaultBonusEditor';
import { useAuth } from '../context/AuthContext';
import { employeeService } from '../services/api';

const EmployeeManagement = () => {
  const { hasPermission } = useAuth();
  const canCreateEmployee = hasPermission('EMPLOYEE_CREATE');
  const [employees, setEmployees] = useState([]);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDocumentsOpen, setIsDocumentsOpen] = useState(false);
  const [selectedEmployeeForDocuments, setSelectedEmployeeForDocuments] = useState(null);
  const [isLeaveFormOpen, setIsLeaveFormOpen] = useState(false);
  const [selectedEmployeeForLeave, setSelectedEmployeeForLeave] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModuleDialogOpen, setIsModuleDialogOpen] = useState(false);
  const [pendingUserAccess, setPendingUserAccess] = useState(null);
  const [isDefaultBonusOpen, setIsDefaultBonusOpen] = useState(false);
  const [selectedEmployeeForBonuses, setSelectedEmployeeForBonuses] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadEmployees();
  }, []);

  const loadEmployees = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await employeeService.getAll();
      // Trier les employés par matricule ascendant
      const sortedEmployees = [...response.data].sort((a, b) => {
        // Gérer les cas où le matricule est null ou undefined
        const matriculeA = a.matricule || '';
        const matriculeB = b.matricule || '';
        // Tri numérique si les deux sont des nombres, sinon tri alphabétique
        const numA = parseInt(matriculeA);
        const numB = parseInt(matriculeB);
        if (!isNaN(numA) && !isNaN(numB)) {
          return numA - numB;
        }
        return matriculeA.localeCompare(matriculeB);
      });
      setEmployees(sortedEmployees);
      setPage(1);
    } catch (err) {
      setError('Erreur lors du chargement des employés');
      console.error('Error loading employees:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedEmployee(null);
    setIsEditing(false);
    setIsFormOpen(true);
  };

  const handleEdit = (employee) => {
    setSelectedEmployee(employee);
    setIsEditing(true);
    setIsFormOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir archiver  cet employé ?')) {
      try {
        await employeeService.delete(id);
        setEmployees(employees.filter(emp => emp.id !== id));
        setError(null);
      } catch (err) {
        const errorMessage = err.response?.data?.error || 'Erreur lors de la suppression';
        setError(errorMessage);
        alert(errorMessage);
      }
    }
  };

  const handleFormSubmit = async (employeeData) => {
    try {
      if (isEditing) {
        await employeeService.update(selectedEmployee.id, employeeData);
        // Mettre à jour l'employé et trier par matricule
        const updatedEmployees = employees.map(emp => 
          emp.id === selectedEmployee.id ? { ...employeeData, id: selectedEmployee.id } : emp
        ).sort((a, b) => {
          const matriculeA = a.matricule || '';
          const matriculeB = b.matricule || '';
          const numA = parseInt(matriculeA);
          const numB = parseInt(matriculeB);
          if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB;
          }
          return matriculeA.localeCompare(matriculeB);
        });
        setEmployees(updatedEmployees);
      } else {
        const response = await employeeService.create(employeeData);
        // Ajouter le nouvel employé et trier par matricule
        const updatedEmployees = [...employees, response.data].sort((a, b) => {
          const matriculeA = a.matricule || '';
          const matriculeB = b.matricule || '';
          const numA = parseInt(matriculeA);
          const numB = parseInt(matriculeB);
          if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB;
          }
          return matriculeA.localeCompare(matriculeB);
        });
        setEmployees(updatedEmployees);
      }
      setIsFormOpen(false);
      setSelectedEmployee(null);
      setError(null);
    } catch (err) {
      const errorMessage = err.response?.data?.error || 
        (err.response?.data && typeof err.response.data === 'object' 
          ? Object.values(err.response.data).join(', ') 
          : 'Erreur lors de l\'enregistrement');
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.trim()) {
      try {
        const response = await employeeService.search(query);
        // Trier les résultats de recherche par matricule ascendant
        const sortedEmployees = [...response.data].sort((a, b) => {
          // Gérer les cas où le matricule est null ou undefined
          const matriculeA = a.matricule || '';
          const matriculeB = b.matricule || '';
          // Tri numérique si les deux sont des nombres, sinon tri alphabétique
          const numA = parseInt(matriculeA);
          const numB = parseInt(matriculeB);
          if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB;
          }
          return matriculeA.localeCompare(matriculeB);
        });
        setEmployees(sortedEmployees);
        setPage(1);
      } catch (err) {
        setError('Erreur lors de la recherche');
        console.error('Error searching employees:', err);
      }
    } else {
      loadEmployees();
    }
  };

  const totalPages = Math.max(1, Math.ceil(employees.length / pageSize));
  const displayedEmployees = employees.slice((page - 1) * pageSize, page * pageSize);
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedEmployee(null);
    setIsEditing(false);
  };

  const handleViewDocuments = (employee) => {
    setSelectedEmployeeForDocuments(employee);
    setIsDocumentsOpen(true);
  };

  const handleCloseDocuments = () => {
    setIsDocumentsOpen(false);
    setSelectedEmployeeForDocuments(null);
  };

  const handleRequestLeave = (employee) => {
    setSelectedEmployeeForLeave(employee);
    setIsLeaveFormOpen(true);
  };

  const handleCloseLeaveForm = () => {
    setIsLeaveFormOpen(false);
    setSelectedEmployeeForLeave(null);
  };

  const handleLeaveRequestSuccess = () => {
    setIsLeaveFormOpen(false);
    setSelectedEmployeeForLeave(null);
  };

  const handleCreateUserAccess = async (employee) => {
    if (!employee) return;

    const defaultUsername = employee.email ? employee.email.split('@')[0] : `${employee.nom}.${employee.prenom}`.toLowerCase();
    const username = window.prompt('Nom d\'utilisateur pour le compte employé', defaultUsername);
    if (!username) {
      return;
    }

    const email = window.prompt('Email du compte (laisser vide pour utiliser l\'email de l\'employé)', employee.email || '');
    const password = window.prompt('Mot de passe temporaire (minimum 6 caractères)');
    if (!password || password.length < 6) {
      alert('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }

    // Ouvrir le dialogue de sélection des modules
    setPendingUserAccess({ employee, username, email, password });
    setIsModuleDialogOpen(true);
  };

  const handleEditDefaultBonuses = (employee) => {
    setSelectedEmployeeForBonuses(employee);
    setIsDefaultBonusOpen(true);
  };

  const handleCloseDefaultBonuses = () => {
    setIsDefaultBonusOpen(false);
    setSelectedEmployeeForBonuses(null);
  };

  const handleEditPermissions = (employee) => {
    if (!employee || !employee.userId) {
      alert("Cet employé n'a pas encore de compte utilisateur.");
      return;
    }
    navigate(`/users/${employee.userId}/permissions`, {
      state: {
        employeeId: employee.id,
        employeeName: `${employee.nom} ${employee.prenom}`,
        username: employee.userUsername,
        email: employee.userEmail,
      },
    });
  };

  return (
    <div className="employee-management">
      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      <div className="management-header">
        <div className="search-container">
          <input
            type="text"
            placeholder="Rechercher un employé (nom, prénom, email, matricule, CIN, CNSS)..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="search-input"
          />
        </div>
        {canCreateEmployee && (
          <button onClick={handleAdd} className="btn btn-primary">
            + Ajouter un employé
          </button>
        )}
      </div>

      {loading ? (
        <div className="loading">Chargement...</div>
      ) : (
        <EmployeeList
          employees={displayedEmployees}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onViewDocuments={handleViewDocuments}
          onRequestLeave={handleRequestLeave}
          onCreateUserAccess={handleCreateUserAccess}
          onEditPermissions={handleEditPermissions}
          onEditDefaultBonuses={handleEditDefaultBonuses}
        />
      )}

      {/* Pagination */}
      {!loading && employees.length > 0 && (
        <div className="pagination" style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button className="btn btn-secondary" disabled={page === 1} onClick={() => handlePageChange(page - 1)}>
            Précédent
          </button>
          <span>Page {page} / {totalPages}</span>
          <button className="btn btn-secondary" disabled={page === totalPages} onClick={() => handlePageChange(page + 1)}>
            Suivant
          </button>
        </div>
      )}

      {isFormOpen && (
        <EmployeeForm
          employee={selectedEmployee}
          isEditing={isEditing}
          onSubmit={handleFormSubmit}
          onClose={handleCloseForm}
        />
      )}

      {isDocumentsOpen && selectedEmployeeForDocuments && (
        <div className="modal-overlay" onClick={handleCloseDocuments}>
          <div className="modal-content documents-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Documents - {selectedEmployeeForDocuments.nom} {selectedEmployeeForDocuments.prenom}</h2>
              <button className="close-button" onClick={handleCloseDocuments}>×</button>
            </div>
            <div className="modal-body">
              <DocumentManager
                employeeId={selectedEmployeeForDocuments.id}
                employeeName={`${selectedEmployeeForDocuments.nom} ${selectedEmployeeForDocuments.prenom}`}
              />
            </div>
          </div>
        </div>
      )}

      {isLeaveFormOpen && selectedEmployeeForLeave && (
        <div className="modal-overlay" onClick={handleCloseLeaveForm}>
          <div className="modal-content leave-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Demande de congé</h2>
              <button className="close-button" onClick={handleCloseLeaveForm}>×</button>
            </div>
            <div className="modal-body">
              <LeaveBalance employeeId={selectedEmployeeForLeave.id} />
              <LeaveRequestForm
                employeeId={selectedEmployeeForLeave.id}
                employeeName={`${selectedEmployeeForLeave.nom} ${selectedEmployeeForLeave.prenom}`}
                onSuccess={handleLeaveRequestSuccess}
                onClose={handleCloseLeaveForm}
              />
            </div>
          </div>
        </div>
      )}

      {isDefaultBonusOpen && selectedEmployeeForBonuses && (
        <EmployeeDefaultBonusEditor
          employee={selectedEmployeeForBonuses}
          onClose={handleCloseDefaultBonuses}
        />
      )}

      {isModuleDialogOpen && pendingUserAccess && (
        <ModulePermissionDialog
          employee={pendingUserAccess.employee}
          initialPermissions={{}}
          onCancel={() => {
            setIsModuleDialogOpen(false);
            setPendingUserAccess(null);
          }}
          onSubmit={async (modulePermissions) => {
            try {
              const { employee, username, email, password } = pendingUserAccess;
              await employeeService.createUserAccess(employee.id, {
                username,
                email,
                password,
                modulePermissions,
              });
              alert('Compte utilisateur créé avec succès.');
              setIsModuleDialogOpen(false);
              setPendingUserAccess(null);
              loadEmployees();
            } catch (err) {
              const errorMessage = err.response?.data?.error || 'Erreur lors de la création du compte utilisateur';
              setError(errorMessage);
              alert(errorMessage);
            }
          }}
        />
      )}
    </div>
  );
};

export default EmployeeManagement;

